// Phase 9B — Agent Workload styles.
//
// Token-only CSS. No hex literals. Premium design system.

import type { CSSProperties } from "react";
import { cardLow, cardMedium } from "../../styles/elevation.ts";
import { transitionStandard, transitionFast } from "../../styles/motion.ts";

// ─── Shell ────────────────────────────────────────────────────

export const agentRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

export const agentHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

export const agentTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.4rem, 3vw, 2rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

export const agentSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: "4px",
};

// ─── Agent cards ──────────────────────────────────────────────

export const agentsRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: "var(--sz-space-3)",
};

export const agentCard: CSSProperties = {
  ...cardMedium,
  ...transitionStandard,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderTop: "3px solid var(--sz-amber)",
};

export const agentCardInactive: CSSProperties = {
  ...agentCard,
  borderTop: "3px solid var(--sz-border-subtle)",
  opacity: 0.6,
};

export const agentAvatar: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "1.8rem",
  width: "48px",
  height: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-amber)",
};

export const agentName: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.1rem",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1.2,
};

export const agentRole: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const agentMeta: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: "var(--sz-space-1)",
};

export const capacityBar: CSSProperties = {
  height: "4px",
  borderRadius: "2px",
  background: "var(--sz-surface-recessed)",
  flex: 1,
  marginRight: "var(--sz-space-2)",
  overflow: "hidden",
};

export const capacityFill: CSSProperties = {
  height: "100%",
  borderRadius: "2px",
  background: "var(--sz-amber)",
  transition: "width 0.3s ease",
};

export const capacityLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
  flexShrink: 0,
};

// ─── Task list ────────────────────────────────────────────────

export const taskGrid: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-1)",
};

export const taskRow: CSSProperties = {
  ...cardLow,
  ...transitionFast,
  display: "grid",
  gridTemplateColumns: "6px 1fr auto auto auto",
  gap: "var(--sz-space-2)",
  alignItems: "center",
  padding: "var(--sz-space-2) var(--sz-space-3)",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-card)",
};

export const taskPriorityDot: CSSProperties = {
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  flexShrink: 0,
};

export const taskTitle: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  fontWeight: 500,
  color: "var(--sz-text-primary)",
};

export const taskAgent: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  minWidth: "80px",
  textAlign: "right",
};

export const taskEffort: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  fontWeight: 600,
  color: "var(--sz-text-secondary)",
  minWidth: "30px",
  textAlign: "center",
};

// ─── Status pills ─────────────────────────────────────────────

const statusBase: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: "var(--sz-radius-sm)",
  minWidth: "70px",
  textAlign: "center",
};

export const STATUS_STYLES: Record<string, CSSProperties> = {
  unassigned: { ...statusBase, background: "var(--sz-surface-recessed)", color: "var(--sz-text-muted)" },
  assigned: { ...statusBase, background: "rgba(59, 130, 246, 0.12)", color: "var(--sz-info-text)" },
  in_progress: { ...statusBase, background: "rgba(245, 158, 11, 0.12)", color: "var(--sz-amber)" },
  blocked: { ...statusBase, background: "rgba(239, 68, 68, 0.12)", color: "var(--sz-error-text)" },
  completed: { ...statusBase, background: "rgba(34, 197, 94, 0.12)", color: "var(--sz-success-text)" },
  cancelled: { ...statusBase, background: "var(--sz-surface-recessed)", color: "var(--sz-text-muted)", textDecoration: "line-through" },
};

export const PRIORITY_COLORS: Record<string, string> = {
  critical: "var(--sz-error-text)",
  high: "var(--sz-amber)",
  medium: "var(--sz-info-text)",
  low: "var(--sz-text-muted)",
};

// ─── Section ──────────────────────────────────────────────────

export const sectionHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

export const sectionTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.2rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
};

export const sectionCount: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
};

// ─── Stats row ────────────────────────────────────────────────

export const statsRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "var(--sz-space-3)",
};

export const statCard: CSSProperties = {
  ...cardLow,
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  padding: "var(--sz-space-3)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderTop: "3px solid var(--sz-amber)",
};

export const statValue: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.4rem, 3vw, 2rem)",
  letterSpacing: "1px",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

export const statLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

// ─── Tabs ─────────────────────────────────────────────────────

export const tabBar: CSSProperties = {
  display: "flex",
  gap: "2px",
  borderBottom: "1px solid var(--sz-border-subtle)",
};

export const tabItem: CSSProperties = {
  ...transitionFast,
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "var(--sz-space-2) var(--sz-space-3)",
  background: "transparent",
  borderTop: "none",
  borderRight: "none",
  borderLeft: "none",
  borderBottom: "2px solid transparent",
  color: "var(--sz-text-muted)",
  cursor: "pointer",
};

export const tabItemActive: CSSProperties = {
  ...tabItem,
  color: "var(--sz-text-primary)",
  borderBottom: "2px solid var(--sz-amber)",
};

// ─── Empty ────────────────────────────────────────────────────

export const emptyState: CSSProperties = {
  ...cardLow,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-6) var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
};

export const emptyIcon: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "2rem",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
};

export const emptyTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.1rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-secondary)",
};

export const emptyNote: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-muted)",
  textAlign: "center",
  maxWidth: "360px",
};

export const phantomBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
  textAlign: "center",
  padding: "var(--sz-space-2) 0",
};
